import { formatEpochToUTC } from './date';

/**
 * Formats a label by replacing underscores with spaces and capitalizing each word
 * @param {string} label - The label to format
 * @returns Formatted label string
 */
const formatLabelText = (label: string | undefined): string => {
  if (typeof label !== 'string') return '';

  return label.replace(/_/g, ' ').replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());
};

/**
 * Formats a numeric value with optional decimals and unit suffix
 * @param {number | undefined} value - The numeric value to format
 * @param {number} decimals - Number of decimal places
 * @param {string} unit - Optional unit suffix
 * @returns Formatted string
 */
const formatNumericWithUnit = (value: number | undefined, decimals: number, unit: string = ''): string => {
  if (typeof value !== 'number') return 'N/A';

  return `${value.toFixed(decimals)}${unit ? ` ${unit}` : ''}`;
};

/**
 * Formats a property key: value pair to a string for display, converting date fields from epoch.
 * Date field keys identified by passing dateKeys set.
 * Otherwise formats given value as a string.
 * @param {string} key - The property key
 * @param {unknown} value - The property value
 * @param {Set<string> | null} dateKeys - Optional set of property keys that should be formatted as dates
 * @returns {string} Formatted property value or '-' if null
 */
const formatPropertyValue = (key: string, value: unknown, dateKeys?: Set<string> | null): string => {
  if (value == null) return '-';
  if (dateKeys?.has(key) && typeof value === 'number') return formatEpochToUTC(value);
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value || '-';
  return String(value);
};

export { formatLabelText, formatNumericWithUnit, formatPropertyValue };
